import React, { useCallback, useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import * as XLSX from 'xlsx';
import { Loader } from 'react-feather';
import { useDropzone } from 'react-dropzone';
import { CheckCircle, FileCsv, XCircle } from 'phosphor-react';
import Papa from 'papaparse';

import { convertKeysToCamelCase, makeChunks } from '@dill/dill-shared';
import { DillColumn, DillTable } from '../../../../general/Dill/DillTable';
import { DillModal } from '../../../../general/Dill/DillModal';
import { DillButton } from '../../../../general/Dill/DillButton';

import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { openMessageModal } from '../../../../../redux/globalSlices/genericSlice';
import {
  getTradeReferences,
  uploadTradeReferences,
} from '../../../../../redux/services/tradeReferenceService';
import { firebaseLargeFileUploadHelper } from '../../../../../utils/helpers';
import { COLORS } from '../../../../../utils/colors';

interface CaseContainsRequiredProperties {
  data: string[];
}

const baseStyle = {
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 4,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const focusedStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};
export const AppTradeRefCSVModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const dispatch = useAppDispatch();

  const [mode, setMode] = useState<'UPLOADING' | 'PROCESSING' | 'WAITING FILE' | 'VIEW_DATA'>(
    'WAITING FILE'
  );
  const [tradeReferenceData, setTradeReferenceData] = useState<any[]>([]);
  const [fileNumber, setFileNumber] = useState(0);
  const [count, setCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadingData, setUploadingData] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<
    { fileName: string; fileType: string; file: File; mimeType: string }[]
  >([]);
  const [fileErrors, setFileErrors] = useState<{ errorMessage: string; fileName: string }[]>([]);
  const [curentFileUploaded, setCurentFileUploaded] = useState<{ name: string; size: number }>();
  const [rejectedFiles, setRejectedFiles] = useState<{ fileName: string; file: File }[]>([]);

  const { user } = useAppSelector((state: any) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const currentMonth = moment().format('MMM-YYYY');
  const folderName = `tradeUploadJsonData/${currentMonth}/${new Date().getTime()}`;
  const folderExcelName = `tradeUploadExcelData/${currentMonth}/${
    currentSupplier?.id ?? ''
  }/${new Date().getTime()}`;

  useEffect(() => {
    if (open) {
      setUploadProgress(0);
      setFileNumber(0);
      setCount(0);
      setMode('WAITING FILE');
      setTradeReferenceData([]);
      setUploadedFiles([]);
      setFileErrors([]);
    }

    return () => {};
  }, [open]);

  const columns = useMemo<DillColumn<any>[]>(() => {
    return [
      {
        key: 'name',
        label: 'Name',
        width: 150,
        render: (_: any, row: any) => <p className="text-xs w-full">{row.name || '-'}</p>,
      },
      {
        key: 'emails',
        label: 'Email',
        width: 180,
        render: (_: any, row: any) => <p className="text-xs w-full">{row.emails || '-'}</p>,
      },
      {
        key: 'address',
        label: 'Address',
        width: 120,
        render: (_: any, row: any) => <p className="text-xs w-full">{row.address || '-'}</p>,
      },
      {
        key: 'phoneNumbers',
        label: 'Phone Number',
        width: 120,
        render: (_: any, row: any) => <p className="text-xs w-full">{row.phoneNumbers || '-'}</p>,
      },
    ];
  }, [open]);

  const containsRequiredProperties = ({ data }: CaseContainsRequiredProperties): boolean => {
    const fieldsToFind = ['name', 'email'];

    const normalizedData = data.map((item) => item.trim().toLowerCase());

    const missingFields = fieldsToFind.filter(
      (field) => !normalizedData.some((column) => column.includes(field))
    );

    return missingFields.length === 0;
  };

  const readFileAsBinaryString = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onprogress = (event: ProgressEvent<FileReader>) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(percentCompleted);
        }
      };
      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event.target && event.target.result) {
          resolve(event.target.result.toString());
        } else {
          reject(new Error('File reading failed'));
        }
      };
      reader.onerror = () => {
        reject(new Error('File reading failed'));
      };
      reader.readAsBinaryString(file);
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[] | null, fileRejections: any[]) => {
    setMode('UPLOADING');

    try {
      setFileErrors([]);

      if (acceptedFiles) {
        const filesToReject: { fileName: string; file: File }[] = [];

        for (const file of acceptedFiles) {
          const result: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event: ProgressEvent<FileReader>) => {
              if (event.target?.result) {
                resolve(event.target.result.toString());
              } else {
                reject('Failed to read file');
              }
            };

            reader.onerror = reject;
            reader.readAsBinaryString(file);
          });

          // CSV Upload
          if (file.name.endsWith('.csv')) {
            await new Promise<void>((resolve) => {
              Papa.parse(result, {
                complete: (parsedData) => {
                  const header = parsedData.meta.fields?.map((field: string) =>
                    field.toLowerCase()
                  );

                  const checkIfTradeRefFile = containsRequiredProperties({
                    data: header as string[],
                  });

                  if (checkIfTradeRefFile) {
                    const mimeType = file.type;
                    setUploadedFiles([
                      { fileName: file.name, fileType: 'TRADE_REF', file, mimeType },
                    ]);
                  } else {
                    filesToReject.push({ fileName: file.name, file });
                  }

                  resolve();
                },
                header: true,
              });
            });
          }

          // Excel Upload
          if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
            const workbook = XLSX.read(result, { type: 'binary', cellStyles: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            const header = sheetData.shift() as string[];

            const checkIfTradeRefFile = containsRequiredProperties({
              data: header.map((field) => field.toLowerCase()),
            });

            if (checkIfTradeRefFile) {
              const mimeType = file.type;
              setUploadedFiles([{ fileName: file.name, fileType: 'TRADE_REF', file, mimeType }]);
            } else {
              filesToReject.push({ fileName: file.name, file });
            }
          }
        }

        setRejectedFiles(filesToReject);
      }

      if (fileRejections?.length > 0) {
        const fileReject = fileRejections.map((file) => ({
          errorMessage:
            file.errors[0]?.code === 'file-invalid-type'
              ? 'File should be either .xls, .xlsx, or .csv'
              : file.errors[0]?.message || 'File is rejected',
          fileName: file.file ? file.file.name : '',
        }));

        setFileErrors(fileReject);
      }

      setMode('WAITING FILE');
    } catch (err) {
      dispatch(
        openMessageModal({
          title: 'Error upload file!',
          subTitle: `Contact customer support.`,
          buttonText: 'Close',
          modalType: 'ERROR',
          isSecondButton: false,
          onClose: () => {},
        })
      );

      setMode('WAITING FILE');
      console.log(err);
    }
  }, []);

  const processTradeReferenceData = async (data: any[]) => {
    const fileData = data[0];
    const file = fileData.file;
    const result = await readFileAsBinaryString(file);

    if (file.name.endsWith('.csv')) {
      Papa.parse(result, {
        complete: async (parsedData) => {
          const tradeRefData = convertKeysToCamelCase(parsedData.data as any);
          const processedData = tradeRefData.map((trade: any) => {
            const emails = Object.keys(trade)
              .filter((key) => key.toLowerCase().startsWith('email'))
              .map((key) => (trade[key] ?? '').toString().trim())
              .filter((val) => val.length > 0);

            const phone = Object.keys(trade)
              .filter((key) => key.toLowerCase().startsWith('phone'))
              .map((key) => (trade[key] ?? '').toString().trim())
              .filter((val) => val.length > 0);

            const zipCode = Object.keys(trade)
              .filter((key) => key.toLowerCase().startsWith('zip'))
              .map((key) => (trade[key] ?? '').toString().trim())
              .filter((val) => val.length > 0);

            return {
              name: trade.name,
              emails: emails.join('; '),
              address: trade?.address ?? '',
              city: trade?.city ?? '',
              state: trade?.state ?? '',
              zipCode: zipCode.join('; '),
              phoneNumbers: phone.join('; '),
            };
          });

          setTradeReferenceData(processedData);
          setMode('VIEW_DATA');
        },
        header: true,
        transform: (value, field) => {
          if (field === 'date') {
            return new Date(value);
          }
          return value;
        },
      });
    } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
      const workbook = XLSX.read(result, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const excelData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        raw: false,
        dateNF: 'mm/dd/yyyy',
      });
      const uploadedData = convertKeysToCamelCase(excelData as any);
      const processedData = uploadedData.map((trade: any) => {
        const emails = Object.keys(trade)
          .filter((key) => key.toLowerCase().startsWith('email'))
          .map((key) => (trade[key] ?? '').toString().trim())
          .filter((val) => val.length > 0);

        const phone = Object.keys(trade)
          .filter((key) => key.toLowerCase().startsWith('phone'))
          .map((key) => (trade[key] ?? '').toString().trim())
          .filter((val) => val.length > 0);

        const zipCode = Object.keys(trade)
          .filter((key) => key.toLowerCase().startsWith('zip'))
          .map((key) => (trade[key] ?? '').toString().trim())
          .filter((val) => val.length > 0);

        return {
          name: trade.name,
          emails: emails.join('; '),
          address: trade?.address ?? '',
          city: trade?.city ?? '',
          state: trade?.state ?? '',
          zipCode: zipCode.join('; '),
          phoneNumbers: phone.join('; '),
        };
      });

      setTradeReferenceData(processedData);
      setMode('VIEW_DATA');
    }
  };

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    open: openFilePicker,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    noClick: true,
    noKeyboard: true,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const handleCloseModal = () => {
    setMode('WAITING FILE');
    setUploadedFiles([]);
    setLoading(false);
    setUploadingData(false);
    setFileErrors([]);
    setRejectedFiles([]);
    setCount(0);
    setFileNumber(0);
    handleClose();
  };

  const handleProcessData = async () => {
    try {
      if (uploadedFiles.length === 0) {
        setFileErrors([{ errorMessage: 'No files selected', fileName: '' }]);
        return;
      }
      setLoading(true);
      setMode('PROCESSING');
      await processTradeReferenceData(uploadedFiles);
    } catch (err: any) {
      console.log(err);
      dispatch(
        openMessageModal({
          modalType: 'ERROR',
          title: 'Error occurred',
          subTitle: `${err.message || err}`,
          buttonText: 'Close',
        })
      );
      setMode('WAITING FILE');
    } finally {
      setLoading(false);
    }
  };

  async function uploadChunks(data: any[], fileNamePrefix: string, chunkSize?: number) {
    try {
      if (chunkSize) {
        const chunkedData = makeChunks(data, chunkSize);
        for (const [index, chunk] of chunkedData.entries()) {
          const jsonData = JSON.stringify(chunk, null, 4);
          const blob = new Blob([jsonData], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          await firebaseLargeFileUploadHelper({
            blobUrl: url,
            fileNameWithExtension: `${fileNamePrefix}_${index}.json`,
            storagePath: folderName,
          });
        }
      } else {
        const jsonData = JSON.stringify(data, null, 4);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        await firebaseLargeFileUploadHelper({
          blobUrl: url,
          fileNameWithExtension: `${fileNamePrefix}.json`,
          storagePath: folderName,
        });
      }
    } catch (err: any) {
      console.log(err);
    }
  }

  const handleImportData = async () => {
    try {
      setLoading(true);
      setUploadingData(true);
      setMode('PROCESSING');
      await uploadChunks(tradeReferenceData, 'TRADE_REF', 2000);
      // uploaded excel file;
      try {
        for (const file of uploadedFiles) {
          await firebaseLargeFileUploadHelper({
            blobUrl: URL.createObjectURL(file.file),
            fileNameWithExtension: file.fileName,
            storagePath: folderExcelName,
          });
        }
      } catch (err) {
        console.log(err);
      }

      const result = await dispatch(
        uploadTradeReferences({
          folderPath: folderName,
          folderExcelName,
          supplierId: currentSupplier?.id ?? '',
        })
      );

      if (result.type.includes('fulfilled')) {
        setLoading(false);
        setUploadingData(false);
        await dispatch(getTradeReferences({ supplierId: currentSupplier?.id ?? '' }));
        handleCloseModal();
        setMode('VIEW_DATA');
      } else if (result.type.includes('rejected')) {
        setLoading(false);
        setUploadingData(false);
        setMode('VIEW_DATA');
        const pp = result.payload as any;
        dispatch(
          openMessageModal({
            modalType: 'ERROR',
            title: 'Error while uploading data',
            subTitle: pp || '',
            buttonText: 'Close',
          })
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setUploadingData(false);
      setMode('VIEW_DATA');
      dispatch(
        openMessageModal({
          modalType: 'ERROR',
          title: 'Error while uploading data',
          subTitle: err as string,
          buttonText: 'Close',
        })
      );
    }
  };

  const handleConfirmImport = async () => {
    handleImportData();
  };

  const curentUploadFileProgres = useMemo(() => {
    if (curentFileUploaded?.size) {
      if (curentFileUploaded?.size > 1000000) {
        const uploadedSize = (curentFileUploaded?.size * uploadProgress) / 100000000;
        const currentFileSize = curentFileUploaded?.size / 1000000;
        return `${uploadedSize.toFixed(2)}MB from ${currentFileSize.toFixed(2)} MB`;
      } else {
        const uploadedSize = (curentFileUploaded?.size * uploadProgress) / 100000;
        const currentFileSize = curentFileUploaded?.size / 1000;
        return `${uploadedSize.toFixed(2)}KB from ${currentFileSize.toFixed(2)}KB`;
      }
    }
    return '';
  }, [uploadProgress, curentFileUploaded]);

  const footer = (
    <>
      <DillButton
        label={'Cancel'}
        variant="secondary"
        onClick={handleCloseModal}
        className="mr-2"
      />
      {mode !== 'UPLOADING' && (
        <DillButton
          label={mode === 'WAITING FILE' ? 'Process Files' : 'Import'}
          isLoading={loading}
          isDisabled={loading}
          onClick={() => {
            if (mode === 'WAITING FILE') {
              handleProcessData();
            } else if (mode === 'VIEW_DATA') {
              handleConfirmImport();
            }
          }}
          width={100}
          className="flex justify-center"
        />
      )}
    </>
  );

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Import Trade Reference File"
      maxWidth={mode === 'VIEW_DATA' ? '900px' : '540px'}
      footer={footer}>
      {mode === 'WAITING FILE' && (
        <div className="">
          <div {...getRootProps({ style })} className="flex flex-col">
            <input {...getInputProps()} />
            <p>Choose files or drag 'n' drop some files here</p>
            <div className="text-xs mt-2 TEXT_SECONDARY-CLR">Accepted file .xslx .xls</div>

            <DillButton
              label="Browse Files"
              variant="secondary"
              onClick={openFilePicker}
              className="mt-2"
            />
          </div>
          {uploadedFiles.length > 0 && (
            <div className="border GREY_200-BORDER rounded p-2 mt-2">
              {uploadedFiles.map((uploadedFile, i) => (
                <div key={`uploaded_files_${i}`} className="flex items-center my-1">
                  <div className="w-[22px]">
                    <CheckCircle color={COLORS.SUCCESS_500} size={20} weight="fill" />
                  </div>
                  <div className="capitalize w-[180px] text-xs ml-3">
                    {`${uploadedFile?.fileType?.replaceAll('_', ' ')?.toLowerCase()} file:`}
                  </div>
                  <div>
                    <div className="text-xs">{uploadedFile.fileName}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {rejectedFiles.length > 0 && (
            <div>
              <div className="border ERROR_500-BORDER rounded p-2 mt-2">
                {rejectedFiles.map((rejectedFile, i) => (
                  <div key={`missing_files_${i}`} className="flex items-center my-1">
                    <div className="w-[22px]">
                      <XCircle color={COLORS.ERROR_500} size={20} weight="fill" />
                    </div>
                    <div className="text-xs ml-2">
                      {`${rejectedFile.fileName.toLowerCase()} does not match trade reference file. The file should contain name and email as headers`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {fileErrors.map((fileError, i) => (
            <div key={`file_error_${i}`} className="flex mt-2">
              <div className="text-xs">{`${
                fileError.fileName ? `${fileError.fileName}:` : ''
              } `}</div>
              <div className="text-xs ERROR_500-CLR">{fileError.errorMessage}</div>
            </div>
          ))}
        </div>
      )}
      {mode === 'UPLOADING' && (
        <div className="flex m-4 p-4 outline-dashed rounded outline-2 outline-offset-0 outline-gray-400 items-center flex-col">
          <div
            className="flex PRIMARY_200-BG rounded-full items-center justify-center mb-3"
            style={{
              width: 40,
              height: 40,
              animation: 'spin 2s linear infinite',
            }}>
            <Loader size={24} color={COLORS.PRIMARY_500} />
          </div>
          <div>Please wait your file is uploading. . .</div>
          <div className="w-full border GREY_200-BORDER p-2 mt-2">
            <div className="flex">
              <div className="w-[46px]">
                <FileCsv size={44} />
              </div>
              <div className="ml-2">
                <div className="text-xs">{curentFileUploaded?.name}</div>
                <div className="text-xs">{curentUploadFileProgres}</div>
              </div>
            </div>
            <div className="mt-2 rounded-full GREY_300-BG w-full h-[8px]">
              <div
                className={`mt-2 rounded-full PRIMARY_500-BG w-${count}/${fileNumber} h-[8px]`}
              />
            </div>
          </div>
        </div>
      )}
      {mode === 'PROCESSING' && (
        <div className="flex m-4 p-4 outline-dashed rounded outline-2 outline-offset-0 outline-gray-400 items-center flex-col">
          <div
            className="flex PRIMARY_200-BG rounded-full items-center justify-center mb-3"
            style={{
              width: 100,
              height: 100,
              animation: 'spin 2s linear infinite',
            }}>
            <Loader size={64} color={COLORS.PRIMARY_500} />
          </div>
          <div className="mt-4">{`${
            uploadingData
              ? `Please wait your file${
                  uploadedFiles.length > 1 ? 's are' : ' is'
                } being uploaded . . .`
              : 'Processing data. . .'
          }`}</div>
        </div>
      )}
      {mode === 'VIEW_DATA' && (
        <div className="h-full">
          <DillTable
            columns={columns}
            data={tradeReferenceData}
            rowKey={(_, index) => index}
            pagination
            pageSize={20}
            totalCount={tradeReferenceData.length}
            emptyMessage="No data found for file."
            countLabel="Trade Reference Data"
            striped={false}
            showCount={true}
            resizable
            maxHeight="calc(100vh - 340px)"
          />
        </div>
      )}
    </DillModal>
  );
};
